import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Tabs,
  Tab,
  Grid,
  TextField,
  Button,
  Switch,
  FormControlLabel,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  Person as PersonIcon,
  Notifications as NotificationsIcon,
  Security as SecurityIcon,
  Psychology as PsychologyIcon,
} from '@mui/icons-material';
import { useOktaAuth } from '@okta/okta-react';
import useApi from '../../hooks/useApi';
import { FONT_FAMILY_PRIMARY } from '../../constants/fonts';
import { getApiErrorMessage } from '../../utils/errorMessages';
import { useErrorToast } from '../../components/common';
import useAiModelCatalog from '../../hooks/useAiModelCatalog';
import { useTenant } from '../../contexts/TenantContext';
import './SettingsPage.css';

const SettingsPage = () => {
  const { authState } = useOktaAuth();
  const api = useApi();
  const { hasRole, currentTenant, refreshTenantContext } = useTenant();
  const [tab, setTab] = useState('profile');
  const [settings, setSettings] = useState({
    emailNotifications: true,
    dashboardNotifications: true,
    weeklyReports: false,
  });

  const canManageTenantAiDefault = hasRole('Client Admin') || hasRole('JLL Super Admin');
  // Registry-driven so a model JLL adds appears here without a frontend release
  const { options: aiModelOptions } = useAiModelCatalog({ enabled: canManageTenantAiDefault });
  const [tenantAiModel, setTenantAiModel] = useState('');
  const [tenantAiSaving, setTenantAiSaving] = useState(false);
  const [tenantAiMessage, setTenantAiMessage] = useState(null);
  const [tenantAiError, setTenantAiError] = useState(null);
  const { showErrorToast } = useErrorToast();

  useEffect(() => {
    if (tenantAiError) {
      showErrorToast(tenantAiError);
      setTenantAiError(null);
    }
  }, [tenantAiError, showErrorToast]);

  useEffect(() => {
    const raw = currentTenant?.defaultAiModel ?? currentTenant?.DefaultAIModel;
    if (typeof raw === 'string' && raw.trim()) {
      setTenantAiModel(raw.trim());
    } else {
      setTenantAiModel('');
    }
  }, [currentTenant?.tenantId, currentTenant?.defaultAiModel, currentTenant?.DefaultAIModel]);

  const handleTabChange = (_event, newValue) => {
    setTab(newValue);
  };

  const handleSettingChange = (setting) => (event) => {
    setSettings((prev) => ({
      ...prev,
      [setting]: event.target.checked,
    }));
  };

  const handleSave = async () => {
    try {
      console.log('Saving settings:', settings);
    } catch (error) {
      console.error('Error saving settings:', error);
    }
  };

  const handleSaveTenantAiDefault = useCallback(async () => {
    setTenantAiSaving(true);
    setTenantAiMessage(null);
    setTenantAiError(null);
    try {
      const payload =
        tenantAiModel && tenantAiModel.trim() ? { defaultAiModel: tenantAiModel.trim() } : { defaultAiModel: null };
      await api.put('/settings/tenant-ai-model', payload);
      setTenantAiMessage(
        payload.defaultAiModel
          ? 'Default model saved. New card runs will use this model unless changed in card settings.'
          : 'Cleared to platform default. New cards will use the platform default until you set a tenant default again.'
      );
      if (refreshTenantContext) {
        await refreshTenantContext(currentTenant?.tenantId);
      }
    } catch (err) {
      setTenantAiError(getApiErrorMessage(err, 'Failed to save'));
    } finally {
      setTenantAiSaving(false);
    }
  }, [api, tenantAiModel, refreshTenantContext, currentTenant?.tenantId]);

  const userEmail = authState?.idToken?.claims?.email || '';
  const userName = authState?.idToken?.claims?.name || 'User';

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Typography
        variant="h4"
        component="h1"
        sx={{
          fontFamily: FONT_FAMILY_PRIMARY,
          fontWeight: 600,
          mb: 4,
        }}
      >
        Settings
      </Typography>

      <Paper elevation={2}>
        <Tabs
          value={tab}
          onChange={handleTabChange}
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab icon={<PersonIcon />} label="Profile" value="profile" />
          <Tab icon={<NotificationsIcon />} label="Notifications" value="notifications" />
          <Tab icon={<SecurityIcon />} label="Security" value="security" />
          {canManageTenantAiDefault && (
            <Tab icon={<PsychologyIcon />} label="Tenant AI" value="tenant-ai" />
          )}
        </Tabs>

        <Box sx={{ p: 4 }}>
          {tab === 'profile' && (
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Name"
                  value={userName}
                  disabled
                  sx={{ mb: 2 }}
                />
                <TextField
                  fullWidth
                  label="Email"
                  value={userEmail}
                  disabled
                />
              </Grid>
            </Grid>
          )}

          {tab === 'notifications' && (
            <Box>
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.emailNotifications}
                    onChange={handleSettingChange('emailNotifications')}
                  />
                }
                label="Email Notifications"
                sx={{ mb: 2 }}
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.dashboardNotifications}
                    onChange={handleSettingChange('dashboardNotifications')}
                  />
                }
                label="Dashboard Notifications"
                sx={{ mb: 2 }}
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.weeklyReports}
                    onChange={handleSettingChange('weeklyReports')}
                  />
                }
                label="Weekly Reports"
              />
            </Box>
          )}

          {tab === 'security' && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Security Settings
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Manage your account security and privacy settings
              </Typography>
              <Button
                variant="outlined"
                sx={{
                  borderColor: '#000000',
                  color: '#000000',
                  '&:hover': {
                    borderColor: '#000000',
                    backgroundColor: 'rgba(0,0,0,0.05)',
                  },
                }}
              >
                Change Password
              </Button>
            </Box>
          )}

          {tab === 'tenant-ai' && canManageTenantAiDefault && (
            <Box sx={{ maxWidth: 560 }}>
              <Typography variant="h6" gutterBottom>
                Default AI model for cards
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Applies to this workspace ({currentTenant?.tenantName || 'current tenant'}). When users create or run
                AI cards, this model is used unless they pick another model in the card&apos;s settings.
              </Typography>
              <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                <InputLabel id="tenant-default-ai-label">Default model</InputLabel>
                <Select
                  labelId="tenant-default-ai-label"
                  label="Default model"
                  value={tenantAiModel || ''}
                  onChange={(e) => setTenantAiModel(e.target.value)}
                >
                  <MenuItem value="">
                    <em>Platform default (Claude 4.5 Sonnet)</em>
                  </MenuItem>
                  {aiModelOptions.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {tenantAiMessage && (
                <Alert severity="success" sx={{ mb: 2 }}>
                  {tenantAiMessage}
                </Alert>
              )}
              <Button
                variant="contained"
                onClick={handleSaveTenantAiDefault}
                disabled={tenantAiSaving}
                sx={{
                  backgroundColor: '#000000',
                  '&:hover': { backgroundColor: '#333333' },
                }}
              >
                {tenantAiSaving ? <CircularProgress size={22} color="inherit" /> : 'Save tenant default'}
              </Button>
            </Box>
          )}

          {tab !== 'tenant-ai' && (
            <>
              <Divider sx={{ my: 4 }} />

              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  variant="contained"
                  onClick={handleSave}
                  sx={{
                    backgroundColor: '#000000',
                    '&:hover': {
                      backgroundColor: '#333333',
                    },
                  }}
                >
                  Save Changes
                </Button>
              </Box>
            </>
          )}
        </Box>
      </Paper>
    </Container>
  );
};

export default SettingsPage;
